import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface ReportRef {
  id: number
  status: number
}

interface EntryDetail {
  id: number
  entry_type: number
  start_time: string | null
  entry_text: string | null
  notes: string | null
  user_name: string | null
  evidence_type_names: string[]
  has_photo: boolean
  has_video: boolean
  significant: number
  date_added: string | null
  added_by_name: string | null
  status: number
}

interface Props {
  case_record: CaseRecord
  report: ReportRef
  entry: EntryDetail
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleString()
}

const ENTRY_TYPE_LABELS: Record<number, string> = {
  0: "Original",
  1: "Revision",
  10: "Final",
}

export default function Show({ case_record, report, entry }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: `Report #${report.id}`,
      href: `/cases/${case_record.id}/reports/${report.id}`,
    },
    {
      title: `Entry #${entry.id}`,
      href: `/cases/${case_record.id}/reports/${report.id}/entries/${entry.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/reports/${report.id}`

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this entry?")) {
      router.delete(`${basePath}/entries/${entry.id}`)
    }
  }

  const evidenceItems = entry.evidence_type_names.map((name) => ({
    flag: true,
    label: name,
  }))

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Entry - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Report Entry</h1>
            <span className="bg-hpi-pale-gray text-hpi-dark-gray rounded px-2 py-1 text-sm">
              {ENTRY_TYPE_LABELS[entry.entry_type] || "Unknown"}
            </span>
            {entry.significant === 1 && (
              <span className="rounded bg-purple-100 px-2 py-1 text-sm text-purple-700">
                Significant
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Link
              href={`${basePath}/entries/${entry.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                Entry Text
              </h2>
              <div className="whitespace-pre-wrap">
                {entry.entry_text ?? "(No content)"}
              </div>
            </div>

            {entry.notes && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">Notes</h2>
                <div className="whitespace-pre-wrap">{entry.notes}</div>
              </div>
            )}
          </div>

          <div>
            <div className="border-hpi-light-gray rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-3 font-semibold">Details</h2>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-hpi-med-gray">Start Time</dt>
                  <dd>{formatDateTime(entry.start_time)}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Author</dt>
                  <dd>{entry.user_name ?? "-"}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Added</dt>
                  <dd>
                    {formatDateTime(entry.date_added)}
                    {entry.added_by_name && ` by ${entry.added_by_name}`}
                  </dd>
                </div>
              </dl>
            </div>

            {evidenceItems.length > 0 && (
              <div className="border-hpi-light-gray mt-4 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-3 font-semibold">
                  Evidence
                </h2>
                <div className="flex flex-wrap gap-2">
                  {evidenceItems.map((e) => (
                    <span
                      key={e.label}
                      className="bg-hpi-light-blue text-hpi-dark-blue rounded px-2 py-1 text-xs"
                    >
                      {e.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <Link
            href={basePath}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Report
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
